import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';

import { useEffect, useState } from 'react';

import { Code, CodeCard, CodeDataEditor } from '@/components/Code/Code';
import { SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH } from '@/components/EvaluatorCrudPane/constants';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { TopBarPaneSlot } from '@/components/Pane.utils';
import { GroupedTabs } from '@/design-system';
import { Button } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useInvokeCustomCode } from '@/hooks/useSwr';
import PythonIcon from '@/icons/PythonIcon.svg?react';
import { ExampleSchema, RunSchema } from '@/types/schema';

import {
  DATASET_EVALUATOR_STARTER_CODE,
  DATASET_EVALUATOR_STARTER_CODE_JS,
  EXTENDED_STATS_RUN_SELECT,
  SESSION_EVALUATOR_STARTER_CODE,
  SESSION_EVALUATOR_STARTER_CODE_JS,
} from '../../Pages/Settings/utils/constants';

const SAMPLE_RUN = {
  name: 'Sample Run',
  inputs: {
    input: 'value',
  },
  outputs: {
    output: 'value',
  },
  status: 'success',
  extra: {
    metadata: {
      key: 'value',
    },
  },
  run_type: 'chain',
  session_id: '00000000-0000-0000-0000-000000000000',
  trace_id: '00000000-0000-0000-0000-000000000000',
  reference_example_id: null,
  error: null,
  feedback_stats: {
    correctness: {
      n: 1,
      avg: 1,
      stdev: 0,
      errors: 0,
      comments: ['Sample comment'],
      session_min_score: 0,
      session_max_score: 1,
      values: {},
    },
  },
  total_tokens: 99,
  prompt_tokens: 45,
  completion_tokens: 54,
  total_cost: 0.0006525,
  prompt_cost: 0.0001125,
  completion_cost: 0.00054,
  prompt_token_details: null,
  completion_token_details: null,
  prompt_cost_details: null,
  completion_cost_details: null,
};

const SAMPLE_EXAMPLE = {
  name: 'Sample Example',
  inputs: {
    input: 'value',
  },
  outputs: {
    output: 'value',
  },
  metadata: {
    key: 'value',
  },
};

export const RulesCustomEvaluatorForm = (props: {
  value?: {
    code?: string;
    language?: 'python' | 'javascript';
  };
  onChange: (value: {
    code: string;
    language?: 'python' | 'javascript';
  }) => void;
  variant: 'session' | 'dataset';
  onSave: (value: { code: string; language?: 'python' | 'javascript' }) => void;
  saveMutating?: boolean;
  sampleRun?: RunSchema;
  sampleExample?: ExampleSchema;
  configureRuleComponent?: React.ReactNode;
  defaultShowTestCode?: boolean;
  setPaneWidth?: (width: number) => void;
  includeExtendedStats?: boolean;
}) => {
  const { value: languageFieldSupported } = useOrgConfig(
    OrgConfigs.require_code_evaluator_language_field
  );

  const sampleRunFieldsToDisplay = props.includeExtendedStats
    ? EXTENDED_STATS_RUN_SELECT
    : [
        'name',
        'inputs',
        'outputs',
        'error',
        'extra',
        'is_root',
        'status',
        'run_type',
        'reference_example_id',
        'session_id',
        'trace_id',
      ];
  const [showTestCode, setShowTestCode] = useState(
    props.defaultShowTestCode ?? true
  );
  const sampleExampleFieldsToDisplay = ['inputs', 'outputs', 'metadata'];
  const getDefaultSampleRun = () => {
    return sampleRunFieldsToDisplay.reduce((acc, key) => {
      acc[key] = (props.sampleRun ?? SAMPLE_RUN)[key];
      return acc;
    }, {});
  };
  const [sampleRun, setSampleRun] = useState(getDefaultSampleRun());
  const [newSampleRunHasExtendedStats, setNewSampleRunHasExtendedStats] =
    useState(props.includeExtendedStats ?? false);
  useEffect(() => {
    const newSampleRun = getDefaultSampleRun();
    setSampleRun(newSampleRun);
    setNewSampleRunHasExtendedStats(props.includeExtendedStats ?? false);
  }, [props.includeExtendedStats, props.sampleRun]);

  const [sampleExample, setSampleExample] = useState(
    props.sampleExample
      ? sampleExampleFieldsToDisplay.reduce((acc, key) => {
          acc[key] = props.sampleExample?.[key];
          return acc;
        }, {})
      : SAMPLE_EXAMPLE
  );

  const aceTrigger = useInvokeCustomCode();
  const cleanedTrace = aceTrigger.data?.stacktrace?.replace(
    /File "<exec>", line\b(?=\s)/g,
    'Line'
  );

  // Get current language from props with fallback to python
  const currentLanguage = props.value?.language ?? 'python';

  // Language configuration dictionary
  const languageConfig = {
    python: {
      starterCode:
        props.variant === 'dataset'
          ? DATASET_EVALUATOR_STARTER_CODE
          : SESSION_EVALUATOR_STARTER_CODE,
      codeEditorLanguage: python(),
      icon: <PythonIcon className="h-6 w-6 text-quaternary" />,
      text: 'Python',
      description:
        'All standard Python libraries are supported, along with a few external libraries like numpy and jsonschema.',
    },
    javascript: {
      starterCode:
        props.variant === 'dataset'
          ? DATASET_EVALUATOR_STARTER_CODE_JS
          : SESSION_EVALUATOR_STARTER_CODE_JS,
      codeEditorLanguage: javascript(),
      icon: (
        <div className="flex h-6 w-6 items-center justify-center rounded bg-yellow-500 text-xs font-bold text-white">
          JS
        </div>
      ),
      text: 'JavaScript',
      description:
        'All standard JavaScript libraries are supported, along with Node.js built-in modules.',
    },
  };

  const handleLanguageChange = (language: 'python' | 'javascript') => {
    props.onChange({
      code: languageConfig[language].starterCode,
      language,
    });
  };

  return (
    <div className="flex gap-16 pt-3">
      <TopBarPaneSlot.Fill>
        <div className="mr-10 flex items-center gap-2">
          <Button
            loading={props.saveMutating}
            size="md"
            color="primary"
            onClick={() =>
              props.onSave(
                props.value?.code
                  ? { code: props.value.code, language: props.value.language }
                  : {
                      code: languageConfig[currentLanguage].starterCode,
                      language: currentLanguage,
                    }
              )
            }
          >
            Save
          </Button>
        </div>
      </TopBarPaneSlot.Fill>
      <div className="flex flex-1 flex-col">
        <div className="mb-3 flex h-[40px] items-center justify-between text-lg font-semibold">
          Write the code
          <div className="flex items-center gap-3">
            <GroupedTabs
              value={currentLanguage}
              onChange={(value) => {
                handleLanguageChange(value as 'python' | 'javascript');
              }}
              options={[
                { value: 'python', display: 'Python' },
                ...(languageFieldSupported
                  ? [{ value: 'javascript', display: 'JavaScript' }]
                  : []),
              ]}
              size="sm"
            />
            {!showTestCode && (
              <Button
                size="md"
                loading={aceTrigger.isMutating}
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setShowTestCode(true);
                  props.setPaneWidth?.(SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH);
                  aceTrigger.trigger({
                    json: {
                      code:
                        props.value?.code ??
                        languageConfig[currentLanguage].starterCode,
                      args:
                        props.variant === 'dataset'
                          ? [sampleRun, sampleExample]
                          : [sampleRun],
                      language: currentLanguage,
                    },
                  });
                }}
              >
                Test Code
              </Button>
            )}
          </div>
        </div>
        {props.configureRuleComponent && (
          <>
            <div className="mb-5 flex flex-col gap-2">
              {props.configureRuleComponent}
            </div>
            <div className="pb-2 text-sm font-semibold">Function</div>
          </>
        )}
        <div className="flex gap-4 rounded-lg border border-primary p-4">
          <div className="flex items-center">
            <div className="rounded-lg border border-secondary p-2">
              {languageConfig[currentLanguage].icon}
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex text-sm font-semibold">
              {languageConfig[currentLanguage].text} Code Editor
            </div>
            <div className="text-sm text-disabled">
              {languageConfig[currentLanguage].description}{' '}
              <a
                href="https://docs.langchain.com/langsmith/online-evaluations#configure-a-custom-code-evaluator"
                target="_blank"
                rel="noreferrer"
                className="text-tertiary hover:underline focus:underline"
              >
                See docs for full list.
              </a>
            </div>
          </div>
        </div>
        <div className="pt-2">
          <CodeCard>
            <Code
              language={languageConfig[currentLanguage].codeEditorLanguage}
              value={
                props.value?.code ?? languageConfig[currentLanguage].starterCode
              }
              onChange={(code) =>
                props.onChange({ code, language: currentLanguage })
              }
            />
          </CodeCard>
        </div>
      </div>

      {showTestCode && (
        <div className="flex max-w-[50%] flex-1 flex-col">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold">Test the code</div>
            <Button
              size="md"
              loading={aceTrigger.isMutating}
              variant="outlined"
              color="secondary"
              onClick={() =>
                aceTrigger.trigger({
                  json: {
                    code:
                      props.value?.code ??
                      languageConfig[currentLanguage].starterCode,
                    args:
                      props.variant === 'dataset'
                        ? [sampleRun, sampleExample]
                        : [sampleRun],
                    language: currentLanguage,
                  },
                })
              }
            >
              Test Code
            </Button>
          </div>
          <div className="mt-3 flex flex-col justify-stretch gap-3">
            <div className="flex flex-1 flex-col gap-3">
              <div className="text-sm font-semibold">Input Run</div>
              <CodeCard>
                <CodeDataEditor
                  key={`code-data-editor-${newSampleRunHasExtendedStats}`}
                  language={'json'}
                  maxHeight={props.variant === 'dataset' ? '30vh' : '50vh'}
                  value={sampleRun}
                  onChange={setSampleRun}
                />
              </CodeCard>
            </div>
            {props.variant === 'dataset' && (
              <div className="flex flex-1 flex-col gap-3">
                <div className="text-sm font-semibold">Input Example</div>
                <CodeCard>
                  <CodeDataEditor
                    language={'json'}
                    maxHeight="30vh"
                    value={sampleExample}
                    onChange={setSampleExample}
                  />
                </CodeCard>
              </div>
            )}
          </div>

          {aceTrigger.data && !aceTrigger.isMutating && (
            <div className="flex flex-1 flex-col gap-3">
              <div className="font-semibold">Result</div>
              {aceTrigger.data?.status === 'success' ? (
                <CodeCard>
                  <CodeDataEditor
                    readOnly
                    language={'json'}
                    value={aceTrigger.data.result}
                  />
                </CodeCard>
              ) : aceTrigger.data?.status === 'failed' ? (
                <ExpandableErrorAlert
                  error={cleanedTrace ?? JSON.stringify(aceTrigger.data)}
                />
              ) : aceTrigger.data?.status === 'timeout' ? (
                <ExpandableErrorAlert error="Timed out executing your code" />
              ) : (
                <ExpandableErrorAlert error="Unknown error" />
              )}

              {aceTrigger.data?.logs && aceTrigger.data.logs.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="font-semibold">Logs</div>
                  <CodeCard>
                    <div className="max-h-60 overflow-x-auto overflow-y-auto p-4">
                      <pre className="whitespace-pre-wrap font-mono text-sm">
                        {aceTrigger.data.logs.map(
                          (
                            log: { type: 'stdout' | 'stderr'; content: string },
                            idx: number
                          ) => (
                            <span
                              key={idx}
                              className={
                                log.type === 'stderr' ? 'text-error' : ''
                              }
                            >
                              {log.content}
                              {idx < (aceTrigger.data?.logs?.length ?? 0) - 1 &&
                                '\n'}
                            </span>
                          )
                        )}
                      </pre>
                    </div>
                  </CodeCard>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
